import { greenCheckCircleLg, netBankingIcon, upiLogo } from "~/assets/assets";
import { useClassicContext } from "~/data/classic/classic_context";
import {
  HubbleRoute,
  PageAlignment,
  pushPage,
  setPages,
} from "~/shared_states/modal";
import { BackButton, ButtonRegular } from "~/widgets/button";
import { HighWaitingTimeBankTransfer } from "./waiting_time_bank_transfer";
import { SuccessfulBankTransfer } from "./successful_bank_transfer";
import { FailedBankTransfer } from "./failed_bank_transfer";
import {
  PayoutInstrumentType,
  ValidatePayoutInstrumentBody,
} from "~/server/types/payout";
import { createSignal, Show, onMount } from "solid-js";
import { clientRepo } from "~/server/apis/client_repo";
import { classNames } from "~/utils/etc";
import { createDebounce } from "~/utils/debounce";
import { toRupees } from "~/utils/number";
import { LoadingBankTransfer } from "./loading_bank_transfer";

const isUpiFormatValid = (id: string): boolean => {
  return /^[\w.-]+@[\w.-]+$/i.test(id);
};

export function TransferModeDetails(props: { amount: number }) {
  const [selectedMode, setSelectedMode] =
    createSignal<PayoutInstrumentType>("UPI");
  const context = useClassicContext();
  const [upiId, setUpiId] = createSignal("");
  const [isValidatingUpi, setIsValidatingUpi] = createSignal(false);
  const [isUpiValid, setIsUpiValid] = createSignal<boolean | undefined>(
    undefined
  );
  const [upiErrorMessage, setUpiErrorMessage] = createSignal<
    string | undefined
  >(undefined);

  const [accountNumber, setAccountNumber] = createSignal("");
  const [ifscCode, setIfscCode] = createSignal("");
  const [name, setName] = createSignal("");

  const [debouncedUpiValidation, setDebouncedUpiValidation] =
    createDebounce(500);
  const [debouncedBankValidation, setDebouncedBankValidation] =
    createDebounce(500);

  onMount(async () => {
    try {
      const bankResponse = await clientRepo.getPayoutInstruments(
        "BANK" as PayoutInstrumentType
      );
      const upiResponse = await clientRepo.getPayoutInstruments(
        "UPI" as PayoutInstrumentType
      );

      const bankInstruments = Array.isArray(bankResponse) ? bankResponse : [];
      const upiInstruments = Array.isArray(upiResponse) ? upiResponse : [];

      const allInstruments = [...bankInstruments, ...upiInstruments].sort(
        (a, b) =>
          new Date(b.lastUsedAt || 0).getTime() -
          new Date(a.lastUsedAt || 0).getTime()
      );

      if (allInstruments.length > 0) {
        const firstInstrument = allInstruments[0];
        if (firstInstrument.mode === "BANK") {
          const details = firstInstrument.details as {
            bankAccountNumber: string;
            ifsc: string;
            registeredName: string;
          };
          setAccountNumber(details.bankAccountNumber);
          setIfscCode(details.ifsc);
          setName(details.registeredName);
          setSelectedMode("BANK");
        } else if (firstInstrument.mode === "UPI") {
          const details = firstInstrument.details as {
            vpa: string;
            registeredName?: string;
          };
          if (details.vpa) {
            setUpiId(details.vpa);
            setIsUpiValid(true); // Since it's a saved instrument, it's already validated
            setPayoutInstrument(firstInstrument.id);
          }
          if (details.registeredName) setName(details.registeredName);
          setSelectedMode(firstInstrument.mode);
        }
      }
    } catch (error) {
      console.error("Error fetching payout instruments:", error);
    }
  });

  const [isValidatingBank, setIsValidatingBank] = createSignal(false);
  const [isBankValid, setIsBankValid] = createSignal<boolean | undefined>(
    undefined
  );
  const [bankErrorMessage, setBankErrorMessage] = createSignal<
    string | undefined
  >(undefined);
  const [isProcessingPayout, setIsProcessingPayout] = createSignal(false);
  const [payoutInstrument, setPayoutInstrument] = createSignal<string | null>();

  async function handleCoinPayout(
    instrumentId: string,
    instrumentType: PayoutInstrumentType
  ) {
    setIsProcessingPayout(true);

    setPages([
      {
        builder: () => <LoadingBankTransfer paymentMode={selectedMode()} />,
        name: HubbleRoute.BANK_TRANSFER_LOADER,
        isModal: false,
        isOpen: () => true,
        setIsOpen: () => {},
        isContentVisible: () => true,
        setIsContentVisible: () => {},
        alignment: PageAlignment.bottom,
        offset: { top: 20, right: 20 },
      },
    ]);

    let loadingTimer: NodeJS.Timeout | null = null;
    let isHighWaitingTime = false;

    // Set a timer to show high waiting time screen after 10 seconds
    loadingTimer = setTimeout(() => {
      isHighWaitingTime = true;
      setPages([
        {
          builder: () => <HighWaitingTimeBankTransfer amount={props.amount} />,
          name: HubbleRoute.BANK_TRANSFER_HIGH_WAITING_TIME,
          isModal: false,
          isOpen: () => true,
          setIsOpen: () => {},
          isContentVisible: () => true,
          setIsContentVisible: () => {},
          alignment: PageAlignment.bottom,
          offset: { top: 20, right: 20 },
        },
      ]);
    }, 10000);

    try {
      const payoutBody = {
        amount: props.amount,
        paymentType: instrumentType,
        paymentInstrumentId: instrumentId,
      };
      const payoutRes = await clientRepo.initiateCoinPayout(payoutBody);

      if (payoutRes.status === "INITIATED") {
        const completeBody = {
          payoutStatus: "SUCCESS",
          instrumentId,
          instrumentType,
        };
        const completeRes = await clientRepo.completePayout({
          id: payoutRes.id,
          body: completeBody,
        });

        // Clear timer if set
        if (loadingTimer) {
          clearTimeout(loadingTimer);
        }
        if (completeRes.status === "SUCCESS") {
          const v4HomeResponse = await clientRepo.fetchV4Home();
          context.setClassicContextData((prev) => ({
            ...prev,
            v4HomePage: v4HomeResponse,
          }));

          setPages([
            {
              builder: () => <SuccessfulBankTransfer amount={props.amount} />,
              name: HubbleRoute.BANK_TRANSFER_SUCCESS,
              isModal: false,
              isOpen: () => true,
              setIsOpen: () => {},
              isContentVisible: () => true,
              setIsContentVisible: () => {},
              alignment: PageAlignment.bottom,
              offset: { top: 20, right: 20 },
            },
          ]);
        } else {
          setPages([
            {
              builder: () => <FailedBankTransfer amount={props.amount} />,
              name: HubbleRoute.BANK_TRANSFER_FAIL,
              isModal: false,
              isOpen: () => true,
              setIsOpen: () => {},
              isContentVisible: () => true,
              setIsContentVisible: () => {},
              alignment: PageAlignment.bottom,
              offset: { top: 20, right: 20 },
            },
          ]);
        }
      } else {
        // Show failure screen if initiation status is not INITIATED
        setPages([
          {
            builder: () => <FailedBankTransfer amount={props.amount} />,
            name: HubbleRoute.BANK_TRANSFER_FAIL,
            isModal: false,
            isOpen: () => true,
            setIsOpen: () => {},
            isContentVisible: () => true,
            setIsContentVisible: () => {},
            alignment: PageAlignment.bottom,
            offset: { top: 20, right: 20 },
          },
        ]);
      }
    } catch (error) {
      console.error("Failed to process payout:", error);

      // Clear timer if set
      if (loadingTimer) {
        clearTimeout(loadingTimer);
      }

      if (instrumentType === "UPI") {
        setUpiErrorMessage("Failed to process UPI payment");
      } else {
        setBankErrorMessage("Failed to process bank payment");
      }

      // Show failure screen
      pushPage(
        () => <FailedBankTransfer amount={props.amount} />,
        {
          isModal: false,
          alignment: PageAlignment.bottom,
          offset: { top: 20, right: 20 },
        },
        HubbleRoute.BANK_TRANSFER_FAIL
      );
    } finally {
      setIsProcessingPayout(false);
    }
  }

  const validateUpiId = async (id: string): Promise<boolean> => {
    if (!isUpiFormatValid(id)) return false;

    try {
      const validatePayoutBody: ValidatePayoutInstrumentBody = {
        type: "UPI",
        upiDetails: {
          upiId: id,
          name: name(),
        },
      };
      const response =
        await clientRepo.validatePayoutInstrument(validatePayoutBody);
      if (response.status === "FAILED") {
        setIsUpiValid(false);
        setUpiErrorMessage(response.message || "Invalid UPI ID");
        return false;
      }
      setIsUpiValid(true);
      setUpiErrorMessage(undefined);
      setPayoutInstrument(response.payoutInstrumentId);
      return true;
    } catch (error: unknown) {
      setIsUpiValid(false);
      if (error instanceof Error) {
        setUpiErrorMessage(error.message);
      } else {
        setUpiErrorMessage("Invalid UPI ID");
      }
      return false;
    }
  };

  const validateAccountNumber = (num: string): boolean => {
    // Basic account number validation - should be between 9-18 digits
    return /^\d{9,18}$/.test(num);
  };

  const validateIfscCode = (code: string): boolean => {
    // IFSC code validation - 11 characters, first 4 alphabets, last 6 numeric
    return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(code);
  };

  const validateBankDetails = async (): Promise<boolean> => {
    if (
      !name() ||
      !validateAccountNumber(accountNumber()) ||
      !validateIfscCode(ifscCode())
    ) {
      return false;
    }

    try {
      const validatePayoutBody: ValidatePayoutInstrumentBody = {
        type: "BANK",
        bankDetails: {
          accountNumber: accountNumber(),
          ifscCode: ifscCode(),
          name: name(),
        },
      };
      const response =
        await clientRepo.validatePayoutInstrument(validatePayoutBody);
      if (response.status === "FAILED") {
        setIsBankValid(false);
        setBankErrorMessage(response.message || "Invalid bank details");
        return false;
      }
      setIsBankValid(true);
      setPayoutInstrument(response.payoutInstrumentId);
      setBankErrorMessage(undefined);
      return true;
    } catch (error: unknown) {
      setIsBankValid(false);
      if (error instanceof Error) {
        setBankErrorMessage(error.message);
      } else {
        setBankErrorMessage("Invalid bank details");
      }
      return false;
    }
  };

  return (
    <div class="relative flex h-full w-full flex-col items-start justify-start bg-baseTertiaryMedium">
      <div class=" sticky top-0 z-10 flex w-full items-center gap-4 border-b border-gray-200  px-4 py-4">
        <BackButton />
      </div>

      <div class="w-full px-4 py-6">
        <p class="mt-4 text-normal text-textDark">
          Transfer {toRupees(props.amount)} to bank
        </p>
        <h2 class="mb-5 mt-1 text-h3 text-textDark">Select mode</h2>

        <div class="flex flex-col gap-3">
          <div
            class={classNames(
              "flex cursor-pointer flex-col items-center justify-between rounded-2xl border border-baseTertiaryDark bg-baseTertiaryLight px-4 py-3"
            )}
            onClick={() => setSelectedMode("UPI")}
          >
            <div
              class="flex w-full items-center justify-between gap-2"
              classList={{
                "pb-3": selectedMode() === "UPI",
              }}
            >
              <div class="flex items-center gap-1 ">
                <img
                  src={upiLogo}
                  class="aspect-square h-8 w-8 rounded-full border border-baseTertiaryMedium"
                  alt="upi logo"
                />{" "}
                <span class="text-bold text-textDark">UPI</span>
              </div>
              <div
                class={classNames(
                  "h-6 w-6 rounded-full border-[1.5px] border-baseSecondaryLight",
                  selectedMode() === "UPI"
                    ? "border-green-600 bg-green-600"
                    : "border-gray-300"
                )}
              >
                <Show when={selectedMode() === "UPI"}>
                  <img src={greenCheckCircleLg} alt="" />
                </Show>
              </div>
            </div>

            <Show when={selectedMode() === "UPI"}>
              <div class="w-full border-t border-baseTertiaryMedium pt-4">
                <div class="relative">
                  <input
                    type="text"
                    value={upiId()}
                    onInput={(e) => {
                      const newValue = e.currentTarget.value;
                      setUpiId(newValue);
                      // Reset validation state
                      setIsUpiValid(undefined);
                      setUpiErrorMessage(undefined);

                      if (newValue) {
                        if (isUpiFormatValid(newValue)) {
                          setIsValidatingUpi(true);
                          setDebouncedUpiValidation({
                            callback: async () => {
                              await validateUpiId(newValue);
                              setIsValidatingUpi(false);
                            },
                          });
                        } else {
                          setIsValidatingUpi(false);
                        }
                      } else {
                        setUpiId("");
                        setIsValidatingUpi(false);
                      }
                    }}
                    placeholder="Enter your UPI ID"
                    class={classNames(
                      "h-[52px] w-full rounded-xl border border-baseTertiaryDark p-4 placeholder:text-normal placeholder:font-medium placeholder:text-textNormal focus:outline-none",
                      upiId() && isUpiValid() === false
                        ? "border-errorDark"
                        : "border-gray-300"
                    )}
                  />
                  <Show when={isValidatingUpi()}>
                    <p class="pt-2 text-f12 text-textNormal">
                      Validating UPI ID...
                    </p>
                  </Show>
                  <Show
                    when={
                      !isValidatingUpi() && upiId() && isUpiValid() === false
                    }
                  >
                    <p class="pt-2 text-f12 text-errorDark">
                      {upiErrorMessage() || "Invalid UPI ID"}
                    </p>
                  </Show>
                  <Show when={!isValidatingUpi() && !upiId()}>
                    <p class="pt-2 text-f12 text-textNormal">
                      The UPI ID is in format of name/phone number@bank
                    </p>
                  </Show>
                </div>

                <ButtonRegular
                  isEnabled={
                    Boolean(upiId()) &&
                    isUpiValid() &&
                    !isValidatingUpi() &&
                    !isProcessingPayout()
                  }
                  onClick={async () => {
                    if (payoutInstrument()) {
                      await handleCoinPayout(payoutInstrument() ?? "", "UPI");
                    }
                  }}
                  class="mt-4 w-full"
                >
                  {isProcessingPayout() ? "Processing..." : "Continue"}
                </ButtonRegular>
              </div>
            </Show>
          </div>

          <div
            class={classNames(
              "flex cursor-pointer flex-col items-center justify-between rounded-2xl border border-baseTertiaryDark bg-baseTertiaryLight px-4 py-3"
            )}
            onClick={() => setSelectedMode("BANK")}
          >
            <div
              class="flex w-full items-center justify-between gap-2"
              classList={{
                "pb-3": selectedMode() === "BANK",
              }}
            >
              <div class="flex items-center gap-2">
                <img
                  src={netBankingIcon}
                  class="aspect-square h-8 w-8 rounded-full border border-baseTertiaryMedium p-1.5"
                  alt="net banking logo"
                />
                <span class="text-bold text-textDark">Net banking</span>
              </div>

              <div
                class={classNames(
                  "h-6 w-6 rounded-full border-[1.5px] border-baseSecondaryLight",
                  selectedMode() === "BANK"
                    ? "border-green-600 bg-green-600"
                    : "border-gray-300"
                )}
              >
                <Show when={selectedMode() === "BANK"}>
                  <img src={greenCheckCircleLg} alt="" />
                </Show>
              </div>
            </div>
            <Show when={selectedMode() === "BANK"}>
              <div class="w-full border-t border-baseTertiaryMedium pt-4">
                <div class="flex flex-col gap-4">
                  <div>
                    <p class="mb-2 text-mediumBold text-textDark">Name</p>
                    <input
                      type="text"
                      placeholder="Enter your name"
                      value={name()}
                      class="h-[52px] w-full rounded-xl border border-baseTertiaryDark p-4 placeholder:text-normal placeholder:font-medium placeholder:text-textNormal focus:outline-none"
                      onInput={(e) => {
                        const newValue = e.currentTarget.value;
                        setName(newValue);
                        if (newValue && selectedMode() === "BANK") {
                          setDebouncedBankValidation({
                            callback: async () => {
                              await validateBankDetails();
                            },
                          });
                        }
                      }}
                    />
                  </div>

                  <div>
                    <p class="mb-2 text-mediumBold text-textDark">
                      Account number
                    </p>
                    <input
                      type="text"
                      value={accountNumber()}
                      onInput={(e) => {
                        const newValue = e.currentTarget.value;
                        setAccountNumber(newValue);
                        // Reset validation when input changes
                        setIsBankValid(undefined);
                        setBankErrorMessage(undefined);

                        if (
                          validateAccountNumber(newValue) &&
                          validateIfscCode(ifscCode())
                        ) {
                          setDebouncedBankValidation({
                            callback: async () => {
                              await validateBankDetails();
                            },
                          });
                        }
                      }}
                      placeholder="Enter account number"
                      class={classNames(
                        "h-[52px] w-full rounded-xl border border-baseTertiaryDark p-4 placeholder:text-normal placeholder:font-medium placeholder:text-textNormal focus:outline-none",
                        accountNumber() &&
                          !validateAccountNumber(accountNumber())
                          ? "border-red-500"
                          : "border-baseTertiaryDark"
                      )}
                    />
                    <Show
                      when={
                        accountNumber() &&
                        !validateAccountNumber(accountNumber())
                      }
                    >
                      <p class="pt-2 text-f12 text-errorDark">
                        Account number is invalid
                      </p>
                    </Show>
                  </div>

                  <div>
                    <p class="mb-2 text-mediumBold text-textDark">IFSC code</p>
                    <input
                      type="text"
                      value={ifscCode()}
                      onInput={(e) => {
                        const newValue = e.currentTarget.value.toUpperCase();
                        setIfscCode(newValue);
                        // Reset validation when input changes
                        setIsBankValid(undefined);
                        setBankErrorMessage(undefined);

                        if (
                          validateIfscCode(newValue) &&
                          validateAccountNumber(accountNumber())
                        ) {
                          setDebouncedBankValidation({
                            callback: async () => {
                              await validateBankDetails();
                            },
                          });
                        }
                      }}
                      placeholder="Enter IFSC code"
                      class={classNames(
                        "h-[52px] w-full rounded-xl border border-baseTertiaryDark p-4 placeholder:text-normal placeholder:font-medium placeholder:text-textNormal focus:outline-none",
                        ifscCode() && !validateIfscCode(ifscCode())
                          ? "border-red-500"
                          : "border-baseTertiaryDark"
                      )}
                    />
                    <Show when={ifscCode() && !validateIfscCode(ifscCode())}>
                      <p class="pt-2 text-f12 text-errorDark">
                        IFSC code is invalid
                      </p>
                    </Show>
                  </div>

                  <Show when={isValidatingBank()}>
                    <p class="pt-2 text-f12 text-textNormal">
                      Validating bank details...
                    </p>
                  </Show>
                  <Show when={!isValidatingBank() && isBankValid() === false}>
                    <p class="text-f12 text-errorDark">{bankErrorMessage()}</p>
                  </Show>
                  <ButtonRegular
                    isEnabled={
                      Boolean(accountNumber()) &&
                      validateAccountNumber(accountNumber()) &&
                      Boolean(ifscCode()) &&
                      validateIfscCode(ifscCode()) &&
                      !isValidatingBank() &&
                      !isProcessingPayout()
                    }
                    onClick={async () => {
                      setIsValidatingBank(true);
                      const isValid = await validateBankDetails();
                      setIsValidatingBank(false);

                      if (isValid && payoutInstrument()) {
                        await handleCoinPayout(
                          payoutInstrument() ?? "",
                          "BANK"
                        );
                      }
                    }}
                    class="w-full"
                  >
                    {isProcessingPayout() ? "Processing..." : "Continue"}
                  </ButtonRegular>
                </div>
              </div>
            </Show>
          </div>
        </div>
      </div>
    </div>
  );
}
